import type {TuiCountryIsoCode, TuiLanguage} from '@taiga-ui/i18n/types';

// addon-commerce.ts
import type {TuiLanguageCommerce} from '@taiga-ui/i18n/types';

export const TUI_SWEDISH_LANGUAGE_ADDON_COMMERCE: TuiLanguageCommerce = {
    cardNumber: ['Number', 'Kortnummer'],
    cardExpiry: ['Expiry', 'Utgångsdatum'],
};

// layout.ts
import type {TuiLanguageLayout} from '@taiga-ui/i18n/types';

export const TUI_SWEDISH_LANGUAGE_LAYOUT: TuiLanguageLayout = {
    inputSearch: {
        popular: 'Populärt',
        history: 'Senaste',
        placeholder: 'Sök',
        hotkey: 'för att söka',
        all: 'Alla',
        empty: 'Inga resultat',
    },
};

// kit.ts
import type {TuiLanguageKit} from '@taiga-ui/i18n/types';

export const TUI_SWEDISH_LANGUAGE_KIT: TuiLanguageKit = {
    cancel: 'Avbryt',
    done: 'Klar',
    more: 'Mer',
    otherDate: 'Annat datum...',
    showAll: 'Visa alla',
    hide: 'Dölj',
    mobileCalendarTexts: [
        'Välj dag',
        'Välj period',
        'Välj dagar',
    ],
    range: ['Från', 'Till'],
    countTexts: ['Mer', 'Mindre'],
    time: {
        'HH:MM': 'HH:MM', 
        'HH:MM AA': 'HH:MM AA', 
        'HH:MM:SS': 'HH:MM:SS',
        'HH:MM:SS AA': 'HH:MM:SS AA',
        'HH:MM:SS.MSS': 'HH:MM:SS.MSS',
        'HH:MM:SS.MSS AA': 'HH:MM:SS.MSS AA',
    },
    dateTexts: {
        DMY: 'dag.månad.år', 
        MDY: 'månad.dag.år', 
        YMD: 'år.månad.dag', 
    },
    digitalInformationUnits: ['Byte', 'Kilobyte', 'Megabyte'],
    passwordTexts: ['Visa lösenord', 'Dölj lösenord'],
    copyTexts: ['Kopiera', 'Kopierad'],
    shortCalendarMonths: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'Maj',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dec',
    ],
    pagination: ['Föregående sida', 'Nästa sida'],
    fileTexts: {
        loadingError: 'Laddningsfel',
        preview: 'Förhandsvisa',
        remove: 'Ta bort',
    },
    inputFileTexts: {
        defaultLabelSingle: 'eller släpp den här',
        defaultLabelMultiple: 'eller släpp dem här',
        defaultLinkSingle: 'Välj fil',
        defaultLinkMultiple: 'Välj filer',
        maxSizeRejectionReason: 'Filen är för stor',
        formatRejectionReason: 'Ogiltigt filformat',
        drop: 'Släpp fil här',
        dropMultiple: 'Släpp filer här',
    },
    multiSelectTexts: {
        all: 'Markera alla',
        none: 'Markera inga',
    },
    confirm: {
        yes: 'Ja',
        no: 'Nej',
    },
    previewTexts: {
        rotate: 'Rotera',
    },
    zoomTexts: {
        zoomOut: 'Zooma ut',
        zoomIn: 'Zooma in',
        reset: 'Återställ',
    },
    phoneSearch: 'Sök land eller kod',
};

// addon-editor.ts
import type {TuiLanguageEditor} from '@taiga-ui/i18n/types';

export const TUI_SWEDISH_LANGUAGE_ADDON_EDITOR: TuiLanguageEditor = {
    colorSelectorModeNames: ['Enfärgad', 'Färggradient'],
    toolbarTools: {
        undo: 'Ångra',
        redo: 'Gör om',
        font: 'Teckensnitt',
        fontStyle: 'Teckensnittsstil',
        fontSize: 'Teckenstorlek',
        bold: 'Fet',
        italic: 'Kursiv',
        underline: 'Understruken',
        strikeThrough: 'Genomstruken',
        justify: 'Justera',
        justifyLeft: 'Vänsterjustera',
        justifyCenter: 'Centrera',
        justifyRight: 'Högerjustera',
        justifyFull: 'Marginaljustera',
        list: 'Lista',
        indent: 'Öka indrag',
        outdent: 'Minska indrag',
        unorderedList: 'Punktlista',
        orderedList: 'Numrerad lista',
        quote: 'Citat',
        foreColor: 'Textfärg',
        backColor: 'Bakgrundsfärg',
        hiliteColor: 'Markeringsfärg',
        clear: 'Rensa',
        link: 'Länk',
        attach: 'Bifoga fil',
        tex: 'Infoga TeX',
        code: 'Kod',
        image: 'Infoga bild',
        insertHorizontalRule: 'Infoga horisontell linje',
        superscript: 'Upphöjd text',
        subscript: 'Nedsänkt text',
        insertTable: 'Infoga tabell',
        insertGroup: 'Infoga grupp',
        hiliteGroup: 'Markera grupp',
        removeGroup: 'Ta bort grupp',
        insertAnchor: 'Infoga ankare',
        mergeCells: 'Sammanfoga celler',
        splitCells: 'Dela celler',
        rowsColumnsManaging: 'Hantera rader och kolumner',
        cellColor: 'Cellfärg',
        setDetails: 'Ange detaljer',
        removeDetails: "Ta bort detaljer",
    },
    editorEditLink: {
        urlExample: 'exempel.com',
        anchorExample: 'ankare',
    },
    editorTableCommands: [
        ['Infoga kolumn före', 'Infoga kolumn efter'],
        ['Infoga rad före', 'Infoga rad efter'],
        ['Ta bort kolumn', 'Ta bort rad'],
    ],
    editorCodeOptions: ['Kod i text', 'Kodblock'],
    editorFontOptions: {
        small: 'Liten',
        large: 'Stor',
        normal: 'Normal',
        title: 'Titel',
        subtitle: 'Underrubrik',
    },
};

// addon-table.ts
import type {TuiLanguageTable} from '@taiga-ui/i18n/types';

export const TUI_SWEDISH_LANGUAGE_ADDON_TABLE: TuiLanguageTable = {
    showHideText: 'Visa/Dölj',
    paginationTexts: {
        pages: 'Sidor',
        linesPerPage: 'Rader per sida',
        of: 'av',
    },
};

// core.ts
import type {TuiLanguageCore} from '@taiga-ui/i18n/types';

export const TUI_SWEDISH_LANGUAGE_CORE: TuiLanguageCore = {
    months: [
        'Januari',
        'Februari',
        'Mars',
        'April',
        'Maj',
        'Juni',
        'Juli',
        'Augusti',
        'September',
        'Oktober',
        'November',
        'December',
    ],
    close: 'Stäng',
    clear: 'Rensa',
    nothingFoundMessage: 'Inga resultat',
    defaultErrorMessage: 'Ogiltigt värde',
    spinTexts: ['Föregående', 'Nästa'],
    shortWeekDays: ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'],
    countries: {} as Record<TuiCountryIsoCode, string>,
};

export const TUI_SWEDISH_LANGUAGE: TuiLanguage = {
    name: 'swedish',
    ...TUI_SWEDISH_LANGUAGE_CORE,
    ...TUI_SWEDISH_LANGUAGE_KIT,
    ...TUI_SWEDISH_LANGUAGE_ADDON_TABLE,
    ...TUI_SWEDISH_LANGUAGE_ADDON_COMMERCE,
    ...TUI_SWEDISH_LANGUAGE_ADDON_EDITOR,
    ...TUI_SWEDISH_LANGUAGE_LAYOUT,
};
